import { CheckCircle, Close } from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { I18n, Translate2 } from 'common'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { defaultTheme } from '../../../../../src/theme/index'
import { Button } from '../../../../../src/ui-components/forms/Button'
import { useResultsStore } from '../../store/ResultsStore'
import { zIndex } from '../../../../../src/theme/zIndex'

const SNW_SORT_OPTIONS = new Set(['List', 'Cost', 'Availability'])

export const SortNotificationBanner = observer(() => {
  const resultsStore = useResultsStore()
  const { searchResults, sortOrder, gfxLookupMade } = resultsStore
  const { isSnwFinished, parts } = searchResults

  const [dismissed, setDismissed] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  const isSortRelevant =
    SNW_SORT_OPTIONS.has(sortOrder[0]?.property) ||
    SNW_SORT_OPTIONS.has(sortOrder[1]?.property)

  const hasParts = parts?.length > 0
  const isSnwLoading = !isSnwFinished

  // Gate: only show if sort is relevant and initial results arrived while SNW was still loading
  useEffect(() => {
    if (isSortRelevant && hasParts && isSnwLoading) {
      setShouldRender(true)
      setDismissed(false)
    }
  }, [hasParts, isSnwLoading, isSortRelevant])

  // Reset when a new search starts (parts cleared)
  useEffect(() => {
    if (!hasParts) {
      setShouldRender(false)
      setDismissed(false)
    }
  }, [hasParts])

  if (!shouldRender || dismissed) return null

  const handleSort = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    resultsStore.searchResults.parts = []
    if (gfxLookupMade) {
      resultsStore.searchByGfxPartBin(0)
    } else {
      resultsStore.searchByPartBin(0, undefined, true, true)
    }
    setShouldRender(false)
  }

  return (
    <BannerContainer>
      <FloatingBanner>
        <ContentRow>
          {isSnwLoading ? <Spinner size={18} /> : <StyledCheckCircle />}
          <Message>
            <PrimaryText>
              <I18n name={isSnwLoading ? 'loadingResults' : 'resultsLoaded'} />
            </PrimaryText>
            <SubText>
              <I18n name="notAllItemsSorted" />
            </SubText>
          </Message>
        </ContentRow>
        <ActionsRow>
          {!isSnwLoading && (
            <SortButton
              onClick={handleSort}
              text="sortAllResults"
              variant="secondary"
            />
          )}
          <DismissButton
            onClick={() => setDismissed(true)}
            aria-label={Translate2('dismiss')}
          >
            <Close fontSize="small" />
          </DismissButton>
        </ActionsRow>
      </FloatingBanner>
    </BannerContainer>
  )
})

const BannerContainer = styled.div`
  position: sticky;
  top: 100px;
  z-index: ${zIndex.dropdown - 1};
  display: flex;
  justify-content: center;
  height: 0;
  overflow: visible;
  pointer-events: none;
`

const FloatingBanner = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  min-height: 90px;
  padding: 10px 26px;
  border-radius: 4px;
  gap: 12px;
  background: ${defaultTheme.colors.yellowLight};
  border: 1px solid ${defaultTheme.colors.yellowReSort};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
`

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 10px;
`

const Spinner = styled(CircularProgress)`
  color: ${defaultTheme.colors.yellowReSort} !important;
  flex-shrink: 0;
`

const StyledCheckCircle = styled(CheckCircle)`
  color: ${defaultTheme.colors.green};
  font-size: 28px;
  flex-shrink: 0;
`

const Message = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
  font-family: Roboto, sans-serif;
`

const PrimaryText = styled.span`
  font-size: 16px;
  font-weight: 700;
`

const SubText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${defaultTheme.colors.almostBlack};
`

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const SortButton = styled(Button)`
  white-space: nowrap;
`

const DismissButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  color: ${defaultTheme.colors.greyDark};
  &:hover {
    color: ${defaultTheme.colors.black};
  }
`
